import api, { unwrapList, unwrapOne } from "./apiClient";
import { BACKEND_URL } from "@/config/api";

const API_BASE = `${BACKEND_URL}/companies`;

/**
 * Campos do CompanyRequestDTO com o tamanho máximo aceito pela API.
 * Os nomes seguem o contrato da API (camelCase neste recurso).
 */
const TEXT_FIELDS = {
  name: 120,
  contactName: 50,
  contactPhone: 20,
  description: 500,
  logoUrl: 500,
  deliveryDescription: 300,
  pickupDescription: 300,
  eventsDescription: 300,
  businessHoursWeekdays: 100,
  businessHoursWeekend: 100,
  footerInfo: 200,
  // Dados PIX (bf_company.pix_*): chave e nome/cidade do recebedor no QR code
  pixKey: 77,
  // Gravados completos; no QR code a API abrevia para 25/15 (regra do Bacen)
  pixMerchantName: 120,
  pixMerchantCity: 100,
};

/**
 * Valida a chave PIX com as mesmas regras da API (PixDataHelper.normalizeKey):
 * e-mail, celular +55DDDNÚMERO, CPF/CNPJ (com ou sem pontuação) ou chave aleatória (UUID).
 * @returns {string} mensagem de erro ou "" quando válida
 */
const validatePixKey = (key) => {
  if (key.includes("@")) {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(key)
      ? ""
      : "Chave PIX de e-mail inválida";
  }
  if (/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(key)) {
    return "";
  }
  if (key.startsWith("+")) {
    return /^\+55\d{10,11}$/.test(key.replace(/[\s()-]/g, ""))
      ? ""
      : "Celular deve estar no formato +55DDDNÚMERO (ex.: +5511999999999)";
  }
  if (/^[0-9.\-/\s]+$/.test(key)) {
    const digits = key.replace(/\D/g, "").length;
    return digits === 11 || digits === 14
      ? ""
      : "CPF/CNPJ inválido. Para celular use o formato +55DDDNÚMERO";
  }
  return "Chave PIX inválida. Use CPF/CNPJ, e-mail, celular (+55DDDNÚMERO) ou chave aleatória";
};

/**
 * Company Service
 * Gerenciamento de empresas (CompanyController - /companies):
 * cadastro, disponibilidade (ligar/desligar sistema) e delivery.
 * Este recurso responde sem o envelope { data }.
 */
class CompanyService {
  /**
   * Listar empresas. Owner global: todas; demais perfis: só a própria.
   * @param {object} [options] - config extra do axios
   *   (ex.: { skipCompanyScope: true } para o seletor do owner ignorar a empresa escolhida)
   * @returns {Promise<Array>}
   */
  async getAllCompanies(options = {}) {
    try {
      const response = await api.get(API_BASE, options);
      return unwrapList(response);
    } catch (error) {
      console.error("❌ Erro ao buscar empresas:", error);
      throw error;
    }
  }

  /**
   * Buscar empresa por código (UUID)
   * @param {string} code
   * @returns {Promise<object>}
   */
  async getCompanyByCode(code, options = {}) {
    try {
      const response = await api.get(`${API_BASE}/${code}`, options);
      return unwrapOne(response);
    } catch (error) {
      console.error(`❌ Erro ao buscar empresa ${code}:`, error);
      throw error;
    }
  }

  /**
   * Criar nova empresa
   * @param {object} companyData - CompanyRequestDTO
   * @returns {Promise<object>}
   */
  async createCompany(companyData) {
    try {
      const response = await api.post(
        API_BASE,
        companyData
      );
      return response.data?.data || response.data;
    } catch (error) {
      console.error("❌ Erro ao criar empresa:", error);
      throw error;
    }
  }

  /**
   * Atualizar empresa (campos omitidos mantêm o valor atual)
   * @param {string} code
   * @param {object} companyData - CompanyRequestDTO
   * @returns {Promise<object>}
   */
  async updateCompany(code, companyData) {
    try {
      const response = await api.put(
        `${API_BASE}/${code}`,
        companyData
      );
      return response.data?.data || response.data;
    } catch (error) {
      console.error(`❌ Erro ao atualizar empresa ${code}:`, error);
      throw error;
    }
  }

  /**
   * Ligar/Desligar sistema (loja aberta/fechada)
   * @param {string} code
   * @param {boolean} available
   * @returns {Promise<object>} empresa atualizada
   */
  async setAvailability(code, available) {
    try {
      const response = await api.put(
        `${API_BASE}/${code}/availability`,
        { available }
      );
      return response.data?.data || response.data;
    } catch (error) {
      console.error(`❌ Erro ao alterar disponibilidade ${code}:`, error);
      throw error;
    }
  }

  /**
   * Verificar se o delivery da empresa está habilitado
   * @param {string} code
   * @returns {Promise<boolean>}
   */
  async checkDelivery(code) {
    try {
      const response = await api.get(
        `${API_BASE}/${code}/delivery`
      );
      return response.data === true;
    } catch (error) {
      console.error(`❌ Erro ao verificar delivery ${code}:`, error);
      throw error;
    }
  }

  /**
   * Habilitar/Desabilitar delivery
   * @param {string} code
   * @param {boolean} delivery
   * @returns {Promise<object>} empresa atualizada
   */
  async setDelivery(code, delivery) {
    try {
      const response = await api.put(
        `${API_BASE}/${code}/delivery`,
        { delivery }
      );
      return response.data?.data || response.data;
    } catch (error) {
      console.error(`❌ Erro ao alterar delivery ${code}:`, error);
      throw error;
    }
  }

  /**
   * Monta o payload do CompanyRequestDTO a partir do formulário.
   * - Textos vazios vão como "" para limpar o campo no backend
   *   (pixKey/pixMerchantName/pixMerchantCity "" removem o dado PIX);
   * - contactName vazio vai como null (a API exige no mínimo 3 caracteres).
   * @param {object} formData
   * @returns {object}
   */
  buildPayload(formData) {
    const payload = {
      available: !!formData.available,
      delivery: !!formData.delivery,
    };

    Object.keys(TEXT_FIELDS).forEach((field) => {
      payload[field] = String(formData[field] ?? "").trim();
    });

    if (!payload.contactName) {
      payload.contactName = null;
    }

    return payload;
  }

  /**
   * Valida o formulário com as mesmas regras do CompanyRequestDTO
   * @param {object} data
   * @returns {object} erros por campo
   */
  validateCompanyData(data) {
    const errors = {};
    const value = (field) => String(data[field] ?? "").trim();

    if (!value("name")) {
      errors.name = "Nome é obrigatório";
    } else if (value("name").length < 3) {
      errors.name = "Nome deve ter no mínimo 3 caracteres";
    }

    if (value("contactName") && value("contactName").length < 3) {
      errors.contactName = "Nome do contato deve ter no mínimo 3 caracteres";
    }

    const phoneDigits = value("contactPhone").replace(/\D/g, "");
    if (
      value("contactPhone") &&
      (phoneDigits.length < 10 || phoneDigits.length > 13)
    ) {
      errors.contactPhone = "Informe DDD + número (10 a 13 dígitos)";
    }

    if (value("pixKey")) {
      const pixKeyError = validatePixKey(value("pixKey"));
      if (pixKeyError) {
        errors.pixKey = pixKeyError;
      }
      if (!value("pixMerchantCity")) {
        errors.pixMerchantCity = "Informe a cidade para gerar o PIX";
      }
    }

    if (value("logoUrl") && !/^https?:\/\/.+/i.test(value("logoUrl"))) {
      errors.logoUrl = "Informe uma URL iniciando com http:// ou https://";
    }

    Object.entries(TEXT_FIELDS).forEach(([field, max]) => {
      if (!errors[field] && value(field).length > max) {
        errors[field] = `Máximo de ${max} caracteres`;
      }
    });

    return errors;
  }

  /**
   * A empresa tem os dados mínimos para gerar PIX: chave e cidade
   * (sem nome do recebedor, a API usa o nome da empresa).
   * @param {object} company
   * @returns {boolean}
   */
  isPixConfigured(company) {
    return !!(
      String(company?.pixKey ?? "").trim() &&
      String(company?.pixMerchantCity ?? "").trim()
    );
  }

  /**
   * Formata telefone para exibição: (11) 97688-1046
   * @param {string} phone
   * @returns {string}
   */
  formatPhone(phone) {
    const digits = String(phone || "").replace(/\D/g, "");
    const local = digits.length > 11 ? digits.slice(-11) : digits;
    if (local.length === 11) {
      return `(${local.slice(0, 2)}) ${local.slice(2, 7)}-${local.slice(7)}`;
    }
    if (local.length === 10) {
      return `(${local.slice(0, 2)}) ${local.slice(2, 6)}-${local.slice(6)}`;
    }
    return phone || "";
  }
}

// Export singleton instance
const companyService = new CompanyService();
export default companyService;
